import { parseQty } from "../Epos/utils/cartCalculations";
import { getTelecallerProductDisplayName } from "../Epos/utils/telecallerStockLimit";
import {
  cartLinesShareBuyQtyValidationBucket,
  isCartLineGetOrFreeRole,
} from "../Promotion/utils/promotionOfferProductsShared";

export const EMPTY_SALES_LIMIT_DIALOG = {
  open: false,
  productName: "",
  productCode: "",
  maxQty: 0,
  message: "",
  plainTitle: undefined,
};

export const buildStockLimitDialogState = (sourceItem, cap) => ({
  open: true,
  productName: getTelecallerProductDisplayName(sourceItem),
  productCode: sourceItem?.product_code || sourceItem?.code || "",
  maxQty: cap != null ? Math.floor(cap) : 0,
  message: "",
  plainTitle: undefined,
});

export const buildPurchaseLimitDialogState = (sourceItem, message) => ({
  open: true,
  productName: getTelecallerProductDisplayName(sourceItem),
  productCode: sourceItem?.product_code || sourceItem?.code || "",
  maxQty: undefined,
  message: String(message || "").trim(),
  plainTitle: "Purchase Limit Exceeded",
});

const toFiniteNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Keys we copy from cart lines so UI caps match server validation before a line exists. */
export const MAX_ORDER_QTY_SOURCE_KEYS = [
  "maximumOrderQuantity",
  "maximum_order_quantity",
  "maxOrderQty",
  "max_order_qty",
  "maximumQty",
  "maximum_qty",
  "orderMaxQuantity",
  "order_max_quantity",
  "max_qty",
  "maxQty",
];

/**
 * Overlay max-order fields from any cart line matching this product (same product_id).
 */
export const extractMaximumOrderQuantityFields = (source) => {
  if (!source || typeof source !== "object") return {};
  const out = {};
  for (let i = 0; i < MAX_ORDER_QTY_SOURCE_KEYS.length; i += 1) {
    const k = MAX_ORDER_QTY_SOURCE_KEYS[i];
    const v = source[k];
    if (v !== undefined && v !== null && v !== "") {
      out[k] = v;
    }
  }
  return out;
};

const resolveProductId = (item) =>
  String(item?.product_id ?? item?.productId ?? item?.id ?? "");

const cartLinesMatchProduct = (line, pid) =>
  line && resolveProductId(line) === pid;

const cartLinesMatchReplaceTarget = (line, replaceLine) => {
  if (!line || !replaceLine) return false;
  const pid = resolveProductId(replaceLine);
  if (!cartLinesMatchProduct(line, pid)) return false;
  if (!cartLinesShareBuyQtyValidationBucket(line, replaceLine)) return false;
  if (line.id && replaceLine.id && String(line.id) === String(replaceLine.id)) {
    return true;
  }
  const lineType = String(line.order_type || line.type || "Box");
  const replaceType = String(
    replaceLine.order_type || replaceLine.type || "Box"
  );
  return lineType === replaceType;
};

export const pickCartLineQty = (item, qtyField = "qty") => {
  if (!item) return 0;
  if (qtyField === "allot") {
    const allot = parseFloat(item.allot_qty);
    if (Number.isFinite(allot) && allot >= 0) return allot;
    const accept = parseFloat(item.accept_qty);
    if (Number.isFinite(accept) && accept >= 0) return accept;
    return parseQty(item, item.qty);
  }
  return parseQty(item, item.qty ?? item.quantity ?? 0);
};

export const getCartProductQuantityTotal = (cartItems, productId, options = {}) => {
  const pid = String(productId ?? "");
  if (!pid || !Array.isArray(cartItems)) return 0;
  const qtyField = options.qtyField || "qty";
  const replaceLine = options.replaceLine;
  const replaceQty = options.replaceQty;
  const excludeOfferLines = options.excludeOfferLines !== false;
  const countOfferOnly = options.countOfferOnly === true;
  return cartItems.reduce((sum, line) => {
    if (!cartLinesMatchProduct(line, pid)) return sum;
    const lineIsOffer = isCartLineGetOrFreeRole(line);
    if (countOfferOnly && !lineIsOffer) return sum;
    if (!countOfferOnly && excludeOfferLines && lineIsOffer) return sum;
    let q = pickCartLineQty(line, qtyField);
    if (
      replaceLine &&
      replaceQty != null &&
      cartLinesMatchReplaceTarget(line, replaceLine)
    ) {
      q = replaceQty;
    }
    return sum + (Number.isFinite(q) ? q : 0);
  }, 0);
};

export const validateMaximumOrderQuantityForCartAdd = (
  product,
  cartItems,
  additionalQty,
  options = {}
) => {
  if (!product) return { allowed: true };
  const pid = resolveProductId(product);
  const qtyField = options.qtyField || "qty";
  const lineForLimit = mergeOrderLimitFieldsFromCartLines(
    { ...product, product_id: pid || product.product_id, productId: pid || product.productId },
    cartItems
  );
  const existingTotal = getCartProductQuantityTotal(cartItems, pid, { qtyField });
  const addQty = Number(additionalQty);
  const targetTotal = existingTotal + (Number.isFinite(addQty) ? addQty : 0);
  return validateMaximumOrderQuantity(lineForLimit, targetTotal);
};

export const validateMaximumOrderQuantityForLineQty = (
  item,
  cartItems,
  newLineQty,
  options = {}
) => {
  if (!item) return { allowed: true };
  const pid = resolveProductId(item);
  const qtyField = options.qtyField || "qty";
  const lineForLimit = mergeOrderLimitFieldsFromCartLines(item, cartItems);
  const targetTotal = getCartProductQuantityTotal(cartItems, pid, {
    qtyField,
    replaceLine: item,
    replaceQty: newLineQty,
  });
  return validateMaximumOrderQuantity(lineForLimit, targetTotal);
};

export const mergeOrderLimitFieldsFromCartLines = (catalogProduct, cartLines) => {
  const catalogPid = resolveProductId(catalogProduct);
  if (!catalogProduct || !catalogPid) return catalogProduct;
  if (!Array.isArray(cartLines) || cartLines.length === 0) return catalogProduct;

  const pid = catalogPid;
  const overlay = {};

  for (const line of cartLines) {
    if (!cartLinesMatchProduct(line, pid)) continue;
    // A promotional GET/FREE line (e.g. the auto-applied free unit from a
    // "buy N get 1" promo) can carry its own restrictive max-qty fields
    // tied to that promo's allocation. Merging those onto a fresh add's
    // limit check - the same way getCartProductQuantityTotal already
    // excludes offer lines from the running total above - stops a
    // legitimate new, normal-priced purchase of the same product from
    // being silently capped/rejected by the free line's unrelated limit.
    if (isCartLineGetOrFreeRole(line)) continue;
    for (let i = 0; i < MAX_ORDER_QTY_SOURCE_KEYS.length; i += 1) {
      const k = MAX_ORDER_QTY_SOURCE_KEYS[i];
      const v = line[k];
      if (v !== undefined && v !== null && v !== "") {
        overlay[k] = v;
      }
    }
  }

  return Object.keys(overlay).length ? { ...catalogProduct, ...overlay } : catalogProduct;
};

const readMaxOrderQtyFromFlatItem = (item) => {
  if (!item || typeof item !== "object") return null;
  const candidates = MAX_ORDER_QTY_SOURCE_KEYS.map((k) => item[k]);
  for (let i = 0; i < candidates.length; i += 1) {
    const n = toFiniteNumber(candidates[i]);
    if (n !== null && n > 0) {
      return n;
    }
  }
  return null;
};

/**
 * Resolve per-line maximum order qty from promotion / cart / catalog shapes.
 */
export const getMaximumOrderQuantityFromItem = (item) => {
  if (!item) return null;
  const direct = readMaxOrderQtyFromFlatItem(item);
  if (direct !== null) return direct;
  const nestedSources = [
    item.product,
    item.product_details,
    item.catalog_product,
    item.details,
    item.raw,
  ];
  for (let i = 0; i < nestedSources.length; i += 1) {
    const nested = readMaxOrderQtyFromFlatItem(nestedSources[i]);
    if (nested !== null) return nested;
  }
  return null;
};

const pickFirstDefined = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (v !== undefined && v !== null && v !== "") {
      return v;
    }
  }
  return undefined;
};

/** Overlay catalog + cart limit/stock fields onto promotion buy product rows. */
export const enrichPromotionBuyProduct = (promoProduct, catalogProduct, cartItems) => {
  const fromCart = mergeOrderLimitFieldsFromCartLines(
    promoProduct,
    Array.isArray(cartItems) ? cartItems : []
  );
  if (!catalogProduct) return fromCart;
  return {
    ...fromCart,
    ...extractMaximumOrderQuantityFields(catalogProduct),
    availableBoxQty: pickFirstDefined(
      fromCart.availableBoxQty,
      fromCart.available_box_qty,
      catalogProduct.availableBoxQty,
      catalogProduct.available_box_qty
    ),
    available_box_qty: pickFirstDefined(
      fromCart.available_box_qty,
      fromCart.availableBoxQty,
      catalogProduct.available_box_qty,
      catalogProduct.availableBoxQty
    ),
    availablePieceQty: pickFirstDefined(
      fromCart.availablePieceQty,
      fromCart.available_piece_qty,
      catalogProduct.availablePieceQty,
      catalogProduct.available_piece_qty
    ),
    available_piece_qty: pickFirstDefined(
      fromCart.available_piece_qty,
      fromCart.availablePieceQty,
      catalogProduct.available_piece_qty,
      catalogProduct.availablePieceQty
    ),
    stock: pickFirstDefined(fromCart.stock, catalogProduct.stock),
  };
};

const getProductName = (item) => {
  if (!item) return "Product";
  return item.product_name || item.name || item.productName || "Product";
};

export const buildPurchaseLimitExceededMessage = (item, maxQty) =>
  `You cannot purchase more than ${maxQty} Quantity of ${getProductName(item)}.`;

export const validateMaximumOrderQuantity = (item, targetQty) => {
  const maxQty = getMaximumOrderQuantityFromItem(item);
  if (maxQty === null || maxQty <= 0) {
    return { allowed: true };
  }

  const quantity = toFiniteNumber(targetQty);
  if (quantity === null) {
    return { allowed: true };
  }

  if (quantity > maxQty + 1e-9) {
    return {
      allowed: false,
      message: buildPurchaseLimitExceededMessage(item, maxQty),
    };
  }

  return { allowed: true };
};

export const capQuantityToPurchaseLimit = (item, targetQty) => {
  const maxQty = getMaximumOrderQuantityFromItem(item);
  const parsed = toFiniteNumber(targetQty);
  const quantity = parsed !== null ? Math.max(0, Math.floor(parsed)) : 0;
  if (maxQty === null || maxQty <= 0) {
    return { quantity, wasCapped: false, maxQty: null, message: null };
  }
  const capped = Math.min(quantity, maxQty);
  return {
    quantity: capped,
    wasCapped: capped < quantity,
    maxQty,
    message: capped < quantity ? buildPurchaseLimitExceededMessage(item, maxQty) : null,
  };
};

const toValidPositiveLimit = (value) => {
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.floor(parsed);
};

/**
 * Same max-qty resolution as promotion drawer ProductList (stock cap + purchase limit).
 * getPromotionLineStockCap is injected to avoid a circular import with promotionLineStock.
 */
export const resolvePromotionProductMaxAllowedQty = (
  product,
  cartItems,
  pricingType,
  getLineStockCap
) => {
  if (!product || typeof getLineStockCap !== "function") return null;
  const capRaw = getLineStockCap(product, pricingType);
  const maxQuantityFromStock =
    capRaw != null && Number.isFinite(capRaw)
      ? Math.max(0, Math.floor(capRaw))
      : null;
  const productForOrderLimit = mergeOrderLimitFieldsFromCartLines(
    product,
    Array.isArray(cartItems) ? cartItems : []
  );
  const maxQuantityFromOrder = toValidPositiveLimit(
    getMaximumOrderQuantityFromItem(productForOrderLimit)
  );
  if (maxQuantityFromStock !== null && maxQuantityFromOrder !== null) {
    return Math.min(maxQuantityFromStock, maxQuantityFromOrder);
  }
  return maxQuantityFromStock ?? maxQuantityFromOrder;
};

export const clampToPromotionProductMax = (
  product,
  cartItems,
  pricingType,
  targetQty,
  getLineStockCap
) => {
  if (!product || typeof getLineStockCap !== "function") {
    const parsed = toFiniteNumber(targetQty);
    const quantity = parsed !== null ? Math.max(0, Math.floor(parsed)) : 0;
    return {
      quantity,
      wasCapped: false,
      maxAllowed: null,
      limitType: null,
      stockMax: null,
      purchaseMax: null,
      purchaseMessage: null,
    };
  }

  const capRaw = getLineStockCap(product, pricingType);
  const stockMax =
    capRaw != null && Number.isFinite(capRaw)
      ? Math.max(0, Math.floor(capRaw))
      : null;
  const productForOrderLimit = mergeOrderLimitFieldsFromCartLines(
    product,
    Array.isArray(cartItems) ? cartItems : []
  );
  const purchaseMax = toValidPositiveLimit(
    getMaximumOrderQuantityFromItem(productForOrderLimit)
  );
  const maxAllowed = resolvePromotionProductMaxAllowedQty(
    product,
    cartItems,
    pricingType,
    getLineStockCap
  );
  const parsed = toFiniteNumber(targetQty);
  const quantity = parsed !== null ? Math.max(0, Math.floor(parsed)) : 0;
  if (maxAllowed === null) {
    return {
      quantity,
      wasCapped: false,
      maxAllowed: null,
      limitType: null,
      stockMax,
      purchaseMax,
      purchaseMessage: null,
    };
  }
  const capped = Math.min(quantity, maxAllowed);
  const wasCapped = capped < quantity;
  let limitType = null;
  let purchaseMessage = null;
  if (wasCapped) {
    const exceedsStock = stockMax != null && quantity > stockMax;
    const exceedsPurchase = purchaseMax != null && quantity > purchaseMax;
    if (exceedsStock && exceedsPurchase) {
      limitType = stockMax <= purchaseMax ? "stock" : "purchase";
    } else if (exceedsStock) {
      limitType = "stock";
    } else if (exceedsPurchase) {
      limitType = "purchase";
    } else {
      limitType = "purchase";
    }
    if (limitType === "purchase" && purchaseMax != null) {
      purchaseMessage = buildPurchaseLimitExceededMessage(
        productForOrderLimit,
        purchaseMax
      );
    }
  }
  return {
    quantity: capped,
    wasCapped,
    maxAllowed,
    limitType,
    stockMax,
    purchaseMax,
    purchaseMessage,
  };
};

export const getRequestedCartLineQuantity = (item) => {
  if (!item) return 0;
  return parseQty(item, item.qty ?? item.quantity ?? 0);
};

export const purchaseLimitExceededPopupState = (message) => ({
  open: true,
  message: String(message || "").trim(),
  severity: "warning",
  useErrorDialog: true,
  useStockDialog: false,
  stockDialog: undefined,
  plainTitle: "Purchase Limit Exceeded",
});
